import { apiClient } from '@/services/api';
import type {
  AdminUser,
  AdminUserDetail,
  UserListResponse,
  PlatformStats,
  AdminTemplateListResponse,
  AdminEvaluationListResponse,
  AdminChannel,
  CreateUserPayload,
  UpdateUserPayload,
  AppLog,
  LogListResponse,
  LogQueryParams,
  RegistrationInvitation,
  InvitationListResponse,
  CreateCoachInvitationPayload,
  ApproveUserPayload,
  MetricsResponse,
  MetricsRange,
} from '@/types/admin';
import type { CreditMaintenanceResult } from '@/types/api';

// ─── User Management ──────────────────────────────────────────────────────────

export interface GetUsersParams {
  role?: string;
  search?: string;
  page?: number;
  page_size?: number;
  owner_only?: boolean;
  paying_only?: boolean;
  // Credit-system testing tools (B-2026-0065): scope the list to a channel.
  channel_slug?: string;
  channel_role?: string;
}

export async function getUsers(params?: GetUsersParams): Promise<UserListResponse> {
  const response = await apiClient.get<UserListResponse>('/admin/users', { params });
  return response.data;
}

// ─── Eval-usage reset (credit-system testing tool, B-2026-0065) ────────────────
// Rides the legacy monthly_eval_limit system; part of the pinned backend
// contract (POST /api/admin/users/{user_id}/reset-eval-usage).

export interface ResetEvalUsageResult {
  user_id: string;
  monthly_eval_used: 0;
  monthly_eval_limit: number | null;
}

export async function resetEvalUsage(userId: string): Promise<ResetEvalUsageResult> {
  const { data } = await apiClient.post<ResetEvalUsageResult>(
    `/admin/users/${userId}/reset-eval-usage`,
  );
  return data;
}

export async function getUser(userId: string): Promise<AdminUser> {
  const response = await apiClient.get<AdminUser>(`/admin/users/${userId}`);
  return response.data;
}

export async function getUserDetail(id: string): Promise<AdminUserDetail> {
  const response = await apiClient.get<AdminUserDetail>(`/admin/users/${id}/detail`);
  return response.data;
}

export async function updateUser(userId: string, data: UpdateUserPayload): Promise<AdminUser> {
  const response = await apiClient.patch<AdminUser>(`/admin/users/${userId}`, data);
  return response.data;
}

export async function createUser(data: CreateUserPayload): Promise<AdminUser> {
  const response = await apiClient.post<AdminUser>('/admin/users', data);
  return response.data;
}

export async function deactivateUser(userId: string): Promise<AdminUser> {
  const response = await apiClient.patch<AdminUser>(`/admin/users/${userId}`, {
    is_active: false,
  });
  return response.data;
}

// ─── Platform Stats ────────────────────────────────────────────────────────────

export async function getStats(): Promise<PlatformStats> {
  const response = await apiClient.get<PlatformStats>('/admin/stats');
  return response.data;
}

// ─── Templates ────────────────────────────────────────────────────────────────

export interface GetAllTemplatesParams {
  page?: number;
  page_size?: number;
}

export async function getAllTemplates(
  params?: GetAllTemplatesParams
): Promise<AdminTemplateListResponse> {
  const response = await apiClient.get<AdminTemplateListResponse>('/admin/templates', { params });
  return response.data;
}

// ─── Evaluations ──────────────────────────────────────────────────────────────

export interface GetAllEvaluationsParams {
  user_id?: string;
  template_id?: string;
  page?: number;
  page_size?: number;
}

export async function getAllEvaluations(
  params?: GetAllEvaluationsParams
): Promise<AdminEvaluationListResponse> {
  const response = await apiClient.get<AdminEvaluationListResponse>('/admin/evaluations', {
    params,
  });
  return response.data;
}

// ─── Channels ─────────────────────────────────────────────────────────────────

export async function getAllChannels(): Promise<AdminChannel[]> {
  const response = await apiClient.get<AdminChannel[]>('/admin/channels');
  return response.data;
}

/**
 * Patch a channel's subscription tier / status (platform admin only).
 * Part of the pinned credit-system backend contract:
 * PATCH /api/admin/channels/{channel_id}.
 */
export interface PatchAdminChannelPayload {
  subscription_tier?: 1 | 2 | 3 | null;
  subscription_status?: 'active' | null;
}

export async function patchAdminChannel(
  channelId: string,
  body: PatchAdminChannelPayload,
): Promise<AdminChannel> {
  const { data } = await apiClient.patch<AdminChannel>(
    `/admin/channels/${channelId}`,
    body,
  );
  return data;
}

// ─── Credit maintenance (S18, admin on-demand trigger) ─────────────────────────

export async function runCreditMaintenance(): Promise<CreditMaintenanceResult> {
  const { data } = await apiClient.post<CreditMaintenanceResult>(
    '/admin/credit-maintenance/run',
  );
  return data;
}

// ─── Logs ─────────────────────────────────────────────────────────────────────

export async function getLogs(params: LogQueryParams = {}): Promise<LogListResponse> {
  const response = await apiClient.get<LogListResponse>('/admin/logs', { params });
  return response.data;
}

export async function getLogDetail(logId: string): Promise<AppLog> {
  const response = await apiClient.get<AppLog>(`/admin/logs/${logId}`);
  return response.data;
}

// ─── Approve pending user ──────────────────────────────────────────────────────

export async function approveUser(userId: string, data: ApproveUserPayload): Promise<AdminUser> {
  const response = await apiClient.post<AdminUser>(`/admin/users/${userId}/approve`, data);
  return response.data;
}

// ─── Coach invitation endpoints ────────────────────────────────────────────────

export interface ListCoachInvitationsParams {
  status?: string;
}

export async function createCoachInvitation(
  payload: CreateCoachInvitationPayload
): Promise<RegistrationInvitation> {
  const response = await apiClient.post<RegistrationInvitation>(
    '/coach/registration-invitations',
    payload
  );
  return response.data;
}

export async function listCoachInvitations(
  params?: ListCoachInvitationsParams
): Promise<InvitationListResponse> {
  const query: Record<string, string> = {};
  if (params?.status && params.status !== 'all') {
    query.status = params.status;
  }
  const response = await apiClient.get<InvitationListResponse>(
    '/coach/registration-invitations',
    { params: query }
  );
  return response.data;
}

// ─── Admin Metrics (Track A observability) ───────────────────────────────────

export async function getMetrics(range: MetricsRange = '7d'): Promise<MetricsResponse> {
  const response = await apiClient.get<MetricsResponse>('/admin/metrics', {
    params: { range },
  });
  return response.data;
}
